// Offline sync engine for Pilot v1 offline log syncing
// Automatically retries failed submissions when back online

import { listPendingDailyLogs, markDailyLogSynced, markDailyLogFailed, type OfflineDailyLogEntry } from './offlineOutbox';
import { toast } from 'sonner';

// Max retry attempts before marking as permanently failed
const MAX_ATTEMPTS = 10;

// Helper functions matching DailyLogEntry.tsx
const toOptionalString = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") {
    const s = v.trim();
    return s === "" ? undefined : s;
  }
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : undefined;
  return undefined;
};

const toOptionalNumber = (v: unknown): number | undefined => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const sanitizeDailyLog = (raw: any) => {
  const out: Record<string, any> = {
    wellId: raw.wellId,
    logDate: new Date(raw.logDate),
    status: raw.status
  };

  const assignStr = (k: string, v: unknown) => {
    const s = toOptionalString(v);
    if (s !== undefined) out[k] = s;
  };

  assignStr("logTime", raw.logTime);
  assignStr("operatorInitial", raw.operatorInitial);
  assignStr("remarks", raw.remarks);
  assignStr("editReason", raw.editReason);

  const assignNum = (k: string, v: unknown) => {
    const n = toOptionalNumber(v);
    if (n !== undefined) out[k] = n;
  };

  assignNum("meterReadStart", raw.meterReadStart);
  assignNum("meterReadEnd", raw.meterReadEnd);
  assignNum("gpm", raw.gpm);
  assignNum("hours", raw.hours);

  if (raw.extraJson && Object.keys(raw.extraJson).length > 0) {
    out.extraJson = raw.extraJson;
  }

  // Delete undefined keys
  Object.keys(out).forEach((key) => {
    if (out[key] === undefined) delete out[key];
  });

  return out;
};

// Sync a single offline daily log entry
const syncDailyLogEntry = async (entry: OfflineDailyLogEntry): Promise<boolean> => {
  try {
    const normalizedPayload = sanitizeDailyLog(entry);
    console.log("offlineSync dailyLogs.create payload:", normalizedPayload);

    // Make direct fetch call to tRPC endpoint (same as normal submissions)
    const response = await fetch('/api/trpc/dailyLogs.create', {
      method: 'POST',
      credentials: 'include', // Critical for auth
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ json: normalizedPayload }),
    });

    if (response.ok) {
      // Parse response to check for upsert
      try {
        const result = await response.json();
        const data = result?.result?.data;

        if (data?.success || data?.id) {
          // Success - either new log or upsert
          await markDailyLogSynced(entry.id);
          return true;
        } else {
          throw new Error(data?.message || 'Server response indicates failure');
        }
      } catch (parseError) {
        // If we can't parse the response, assume it's a success since HTTP 200
        await markDailyLogSynced(entry.id);
        return true;
      }
    }

    // Parse error message for server-side failures
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.error?.message || errorData?.message || errorMessage;
    } catch {
      // If can't parse error JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }

    // Determine if this is a permanent failure
    if (response.status === 403 || response.status === 409 || response.status === 423) {
      // Forbidden (locked), conflict, or locked - mark as permanently failed
      const reason = response.status === 403 ? 'Forbidden - insufficient permissions' :
                    response.status === 409 ? 'Conflict - duplicate date entry' :
                    response.status === 423 ? 'Locked - log is locked for audit' : errorMessage;
      await markDailyLogFailed(entry.id, reason);
      toast.error(`Couldn't sync saved log: ${reason}`);
    } else if (entry.attempts >= MAX_ATTEMPTS) {
      // Exceeded max attempts
      await markDailyLogFailed(entry.id, `Max retries exceeded: ${errorMessage}`);
      toast.error('Unable to sync saved log after maximum attempts');
    } else {
      // Temporary failure - will retry
      throw new Error(errorMessage);
    }

    return false;
  } catch (networkError: any) {
    // Network error - keep in pending state for retry
    if (navigator.onLine) {
      // If we're online but still getting network errors, mark as failed
      await markDailyLogFailed(entry.id, `Network error: ${networkError.message}`);
      toast.error('Network connectivity issues - will retry later');
    }
    // If offline, just return false to keep retrying
    return false;
  }
};

// Sync all pending daily logs
export const syncPendingDailyLogs = async (): Promise<void> => {
  if (!navigator.onLine) {
    console.log('[OfflineSync] Skipping sync - offline');
    return;
  }

  try {
    const pendingLogs = await listPendingDailyLogs();
    const pendingOnly = pendingLogs.filter(log => !log.lastError); // Only retry entries that haven't failed

    if (pendingOnly.length === 0) {
      return; // Nothing to sync
    }

    console.log(`[OfflineSync] Attempting to sync ${pendingOnly.length} pending logs`);
    let syncedCount = 0;

    // Process sequentially to avoid overwhelming the server
    for (const logEntry of pendingOnly) {
      try {
        const synced = await syncDailyLogEntry(logEntry);
        if (synced) {
          syncedCount++;
        }
      } catch (error) {
        console.error(`[OfflineSync] Failed to sync log ${logEntry.id}:`, error);
        // Continue with next entry - errors are handled inside syncDailyLogEntry
      }
    }

    if (syncedCount > 0) {
      toast.success(`Synced ${syncedCount} saved log${syncedCount > 1 ? 's' : ''}`);
    }
  } catch (error) {
    console.error('[OfflineSync] Sync process failed:', error);
  }
};

// Initialize sync triggers
let syncIntervalId: number | null = null;

export const initializeOfflineSync = (): void => {
  // Sync on app startup (if online)
  if (navigator.onLine) {
    setTimeout(() => syncPendingDailyLogs(), 2000); // Small delay to let app fully initialize
  }

  // Sync when coming back online
  window.addEventListener('online', () => {
    console.log('[OfflineSync] Back online, starting sync');
    setTimeout(() => syncPendingDailyLogs(), 1000);
  });

  // Periodic sync while online (every 60 seconds)
  const startPeriodicSync = () => {
    if (syncIntervalId) return; // Already running

    syncIntervalId = window.setInterval(() => {
      if (navigator.onLine) {
        syncPendingDailyLogs();
      }
    }, 60000); // Every 60 seconds
  };

  const stopPeriodicSync = () => {
    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }
  };

  // Start/stop periodic sync based on online status
  window.addEventListener('online', startPeriodicSync);
  window.addEventListener('offline', stopPeriodicSync);

  // Start immediately if online
  if (navigator.onLine) {
    startPeriodicSync();
  }
};
